import { z } from "zod";

import {
  ActiveLayerSchema,
  EventTypeSchema,
  PeriodSchema,
  PillarSchema,
  RelTypeSchema,
} from "./enums.js";
import {
  AssessmentSchema,
  EntitySchema,
  EventSchema,
  IndexScoreSchema,
  IndicatorSchema,
  RelationshipSchema,
  SourceSchema,
  TechnologySchema,
  WeightsSchema,
  WeightSetSchema,
} from "./entities.js";

/**
 * The public API contract (MASTER SPEC S34).
 *
 * Every route is read-only. The one POST — `/api/index/simulate` — computes and
 * returns; it stores nothing. A public endpoint with no authentication must not
 * offer a write path, and a weight set the caller invented is a question, not a
 * fact worth persisting.
 */

const Id = z.string().min(1);

/** Guards against an unbounded scan on a public endpoint (MASTER SPEC S36). */
export const MAX_PAGE_SIZE = 200;
export const DEFAULT_PAGE_SIZE = 100;

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  offset: z.coerce.number().int().min(0).default(0),
});

/** ISO-8601 instant used for point-in-time queries. */
const AsOf = z.iso.datetime();

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export const EntitiesQuerySchema = PaginationQuerySchema.extend({
  type: EntitySchema.shape.type.optional(),
  indexEligible: z.stringbool().optional(),
});

export const EntitiesResponseSchema = z.object({
  entities: z.array(EntitySchema),
  total: z.number().int().min(0),
});

export const EntityDetailResponseSchema = z.object({
  entity: EntitySchema,
  assessments: z.array(AssessmentSchema),
});

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export const EventsQuerySchema = PaginationQuerySchema.extend({
  layer: ActiveLayerSchema.optional(),
  eventType: EventTypeSchema.optional(),
  actorId: Id.optional(),
  from: AsOf.optional(),
  to: AsOf.optional(),
});

export const EventsResponseSchema = z.object({
  events: z.array(EventSchema),
  total: z.number().int().min(0),
});

export const EventDetailResponseSchema = z.object({
  event: EventSchema,
  sources: z.array(SourceSchema),
  assessments: z.array(AssessmentSchema),
});

// ---------------------------------------------------------------------------
// Relationships
// ---------------------------------------------------------------------------

export const RelationshipsQuerySchema = PaginationQuerySchema.extend({
  /**
   * Point-in-time filter, read half-open: `validFrom <= asOf < validTo`.
   * Omitting it returns relationships regardless of interval.
   */
  asOf: AsOf.optional(),
  relType: RelTypeSchema.optional(),
  entityId: Id.optional(),
  technologyId: Id.optional(),
});

export const RelationshipsResponseSchema = z.object({
  relationships: z.array(RelationshipSchema),
  total: z.number().int().min(0),
});

// ---------------------------------------------------------------------------
// Index
// ---------------------------------------------------------------------------

export const IndexQuerySchema = z.object({
  weightSetId: Id.default("balanced"),
  period: PeriodSchema.optional(),
});

export const IndexRankingEntrySchema = IndexScoreSchema.extend({
  rank: z.number().int().min(1),
  entityNameKo: z.string(),
  entityNameEn: z.string(),
});

export const IndexResponseSchema = z.object({
  period: z.string(),
  weightSet: WeightSetSchema,
  ranking: z.array(IndexRankingEntrySchema),
});

/**
 * The explanation contract (MASTER SPEC S25).
 *
 * This is the endpoint the whole project exists for: it must carry the chain
 * from a displayed score back to the raw number and the source it came from,
 * with nothing elided in between.
 */
export const IndicatorExplanationSchema = z.object({
  indicatorId: Id,
  pillar: PillarSchema,
  unit: z.string(),
  direction: IndicatorSchema.shape.direction,
  transform: IndicatorSchema.shape.transform,

  sourceId: Id,
  source: SourceSchema,
  isEstimate: z.boolean(),
  assessmentId: Id.nullable(),

  rawValue: z.number(),
  transformedValue: z.number(),

  comparison: z.object({
    entityCount: z.number().int().min(0),
    min: z.number(),
    minEntityId: Id.nullable(),
    max: z.number(),
    maxEntityId: Id.nullable(),
  }),

  degenerate: z.boolean(),
  normalized: z.number(),

  weightWithinPillar: z.number(),
  contributionToPillar: z.number(),
  pillarWeight: z.number(),
  contributionToTotal: z.number(),
});

export const PillarExplanationSchema = z.object({
  pillar: PillarSchema,
  score: z.number().nullable(),
  weight: z.number(),
  observedCount: z.number().int().min(0),
  applicableCount: z.number().int().min(0),
  contributionToTotal: z.number(),
});

export const ExplainResponseSchema = z.object({
  entityId: Id,
  period: z.string(),
  weightSet: WeightSetSchema,
  totalScore: z.number(),
  coverage: z.number(),
  lowConfidence: z.boolean(),
  pillars: z.array(PillarExplanationSchema),
  indicators: z.array(IndicatorExplanationSchema),
});

/** Stateless: computes and returns, writes nothing. */
export const SimulateRequestSchema = z.object({
  weights: WeightsSchema,
  period: PeriodSchema.optional(),
});

export const SimulateResponseSchema = z.object({
  period: z.string(),
  ranking: z.array(IndexRankingEntrySchema),
});

// ---------------------------------------------------------------------------
// Graph / Timeline
// ---------------------------------------------------------------------------

export const GraphQuerySchema = z.object({
  asOf: AsOf.optional(),
  layer: ActiveLayerSchema.optional(),
});

export const GraphResponseSchema = z.object({
  nodes: z.array(EntitySchema),
  edges: z.array(RelationshipSchema),
  technologies: z.array(TechnologySchema),
});

export const TimelineQuerySchema = z.object({
  layer: ActiveLayerSchema.optional(),
  from: AsOf.optional(),
  to: AsOf.optional(),
});

export const TimelineEntrySchema = z.object({
  eventId: Id,
  occurredAt: z.iso.datetime(),
  precision: EventSchema.shape.precision,
  titleKo: z.string(),
  layers: z.array(ActiveLayerSchema),
  eventType: EventTypeSchema,
  severity: EventSchema.shape.severity,
});

export const TimelineResponseSchema = z.object({
  entries: z.array(TimelineEntrySchema),
});

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.enum(["bad_request", "not_found", "internal"]),
    message: z.string(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;
export type EntitiesResponse = z.infer<typeof EntitiesResponseSchema>;
export type EventsResponse = z.infer<typeof EventsResponseSchema>;
export type RelationshipsResponse = z.infer<typeof RelationshipsResponseSchema>;
export type IndexResponse = z.infer<typeof IndexResponseSchema>;
export type ExplainResponse = z.infer<typeof ExplainResponseSchema>;
export type SimulateRequest = z.infer<typeof SimulateRequestSchema>;
export type SimulateResponse = z.infer<typeof SimulateResponseSchema>;
export type GraphResponse = z.infer<typeof GraphResponseSchema>;
export type TimelineResponse = z.infer<typeof TimelineResponseSchema>;
